import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../ui/LanguageSelector';

const navLinks = [
  { to: '/products', labelKey: 'nav.products' },
  { to: '/certificates', labelKey: 'nav.contracts' },
  { to: '/pricing', labelKey: 'nav.pricing' },
];

const supportItems = [
  { to: '/contact', labelKey: 'nav.contactUs' },
  { to: '/troubleshoot', labelKey: 'nav.troubleshoot' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [mobileSupportOpen, setMobileSupportOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const handleVerifyClick = (e, closeMobile = false) => {
    e.preventDefault();
    if (closeMobile) setMobileMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById('verify-certificate')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#verify-certificate');
      setTimeout(() => {
        document.getElementById('verify-certificate')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleCustomersClick = (e, closeMobile = false) => {
    e.preventDefault();
    if (closeMobile) setMobileMenuOpen(false);
    if (location.pathname === '/') {
      document.getElementById('our-customers')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#our-customers');
      setTimeout(() => {
        document.getElementById('our-customers')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSupportOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setSupportOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isSupportActive = supportItems.some((item) => location.pathname === item.to);

  return (
    <nav className="navbar">
      <div className="container navbar-content">
        <Link
          to="/"
          className="navbar-brand"
          onClick={location.pathname === '/' ? e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); } : undefined}
        >
          <div className="navbar-logo">
            <img src="/gigaone-favicon.png" alt="Gigaone" />
          </div>
          <span className="navbar-title">Gigaone</span>
        </Link>

        <div className="navbar-links">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`navbar-link ${location.pathname === link.to ? 'active' : ''}`}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          <a
            href="#verify-certificate"
            className="navbar-link"
            onClick={handleVerifyClick}
          >
            {t('nav.verify')}
          </a>

          <a
            href="#our-customers"
            className="navbar-link"
            onClick={handleCustomersClick}
          >
            Our Customers
          </a>

          {/* Support dropdown */}
          <div className="nav-dropdown-wrapper" ref={dropdownRef}>
            <button
              className={`navbar-link nav-dropdown-trigger${isSupportActive ? ' active' : ''}`}
              onClick={() => setSupportOpen((v) => !v)}
              aria-expanded={supportOpen}
              aria-haspopup="true"
            >
              {t('nav.support')}
              <ChevronDown size={14} className={`nav-chevron${supportOpen ? ' open' : ''}`} />
            </button>
            {supportOpen && (
              <div className="nav-dropdown-menu">
                {supportItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`nav-dropdown-item${location.pathname === item.to ? ' active' : ''}`}
                  >
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>

        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: '1.5rem' }}>
          <LanguageSelector />

          {/* Purchase Access — direct link */}
          {/* <Link
            to="/purchase"
            className={`pa-nav-pill-btn${location.pathname === '/purchase' ? ' pa-nav-pill-active' : ''}`}
          >
            <ShoppingCart size={13} />
            <span>Purchase Access</span>
          </Link> */}

          <div className="mobile-only">
          </div>
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="container">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          <a
            href="#verify-certificate"
            className="mobile-menu-link"
            onClick={(e) => handleVerifyClick(e, true)}
          >
            {t('nav.verify')}
          </a>

          <a
            href="#our-customers"
            className="mobile-menu-link"
            onClick={(e) => handleCustomersClick(e, true)}
          >
            Our Customers
          </a>

          {/* Mobile Purchase Access — direct link */}
          {/* <Link
            to="/purchase"
            className="mobile-menu-link"
            onClick={() => setMobileMenuOpen(false)}
            style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <ShoppingCart size={13} />
            Purchase Access
          </Link> */}

          {/* Mobile Support section */}
          <button
            className="mobile-menu-link mobile-menu-dropdown-trigger"
            onClick={() => setMobileSupportOpen((v) => !v)}
          >
            {t('nav.support')}
            <ChevronDown size={14} className={`nav-chevron${mobileSupportOpen ? ' open' : ''}`} />
          </button>
          {mobileSupportOpen && (
            <div className="mobile-menu-sub">
              {supportItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="mobile-menu-link mobile-menu-sub-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t(item.labelKey)}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
